export function convertAtlanticToUTC(localStr: string): string {
  if (!localStr) return localStr;

  const assumedLocal = new Date(`${localStr}:00`);

  return new Date(assumedLocal.toLocaleString('en-US', { timeZone: 'America/Halifax' })).toISOString();
}

export function convertUTCToAtlantic(utcIsoString: string): string {
  if (!utcIsoString) return '';

  const date = new Date(utcIsoString);

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Halifax',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value;

  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/**
 * Ensures a date string or Date object is properly parsed as UTC
 * Handles both ISO strings with/without 'Z' suffix
 */
const ensureUTCDate = (dateInput: string | Date): Date => {
  if (dateInput instanceof Date) {
    return dateInput;
  }

  // If string doesn't end with 'Z' and doesn't have timezone info, treat as UTC
  let dateString = dateInput;
  if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('GMT')) {
    // Remove any existing 'Z' or timezone info, then add 'Z' to ensure UTC parsing
    dateString = dateString.replace(/Z$/, '') + 'Z';
  }

  return new Date(dateString);
};

/**
 * Format UTC time to Atlantic Time (AST/ADT)
 * Automatically handles Daylight Saving Time:
 * - AST (Atlantic Standard Time): UTC-4 (winter)
 * - ADT (Atlantic Daylight Time): UTC-3 (summer)
 */
export const formatAtlanticTime = (dateInput: string | Date, locale: string = 'en-US'): string => {
  const date = ensureUTCDate(dateInput);

  return date.toLocaleTimeString(locale, {
    timeZone: 'America/Halifax',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * Format UTC date to Atlantic Time zone (AST/ADT)
 * Automatically handles Daylight Saving Time
 */
export const formatAtlanticDate = (dateInput: string | Date, locale: string = 'en-US'): string => {
  const date = ensureUTCDate(dateInput);

  return date.toLocaleDateString(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Halifax',
  });
};

/**
 * Format UTC date and time to Atlantic Time zone (AST/ADT) in a readable format
 * Example output: "Mon, Jan 15, 2025 at 10:00"
 */
export const formatAtlanticDateTime = (dateInput: string | Date, locale: string = 'en-US'): string => {
  const date = ensureUTCDate(dateInput);

  const dateStr = date.toLocaleDateString(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Halifax',
  });

  const timeStr = date.toLocaleTimeString(locale, {
    timeZone: 'America/Halifax',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  // Since 'at' is language dependent, the caller should handle the separator or we use a generic one if not provided.
  // Ideally, the separator comes from the caller (t('at')).
  // For backward compatibility within this function, we default to " " if we want to be safe or keep "at" for en.
  // Actually, let's keep it simple here and return just the formatted parts or let component construct string.
  // BUT existing calls expect full string.

  // Changing signature to accept separator is better.
  return `${dateStr} ${timeStr}`;
};
